import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import SEOHead from "@/components/site/SEOHead";
import DraftBanner from "@/components/site/DraftBanner";
import { breadcrumbs } from "@/lib/schema";
import Reveal from "@/components/site/Reveal";
import { useEnquiry } from "@/components/site/PublicLayout";
import SmartMedia from "@/components/site/SmartMedia";
import { mediaImage } from "@/lib/media";

export function BlogList() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/public/blog").then((r) => setPosts(r.data || [])); }, []);

  return (
    <>
      <SEOHead title="Stories & Real Weddings — Happy Retreats" description="Real wedding stories, venue guides, corporate retreat journals from Happy Retreats." />
      <section className="container-x pt-40 pb-16" data-testid="blog-header">
        <div className="overline">The journal</div>
        <h1 className="display-1 mt-8 max-w-4xl">Real stories, slowly told.</h1>
        <p className="body-lead mt-8 max-w-2xl">A growing archive of real weddings, behind-the-property guides and slow-travel essays from Happy Retreats.</p>
      </section>

      <section className="container-x pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-14">
          {posts.map((p, i) => (
            <Reveal key={p.id} delay={i * 80}>
              <Link to={`/blog/${p.slug}`} className="block group" data-testid={`blog-list-${i}`}>
                <div className="aspect-[4/3] overflow-hidden zoom-on-hover relative">
                  <SmartMedia media={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
                </div>
                <div className="overline mt-6">{p.category}</div>
                <h2 className="font-display text-2xl md:text-3xl mt-3 leading-tight group-hover:text-clay transition-colors">{p.title}</h2>
                <p className="mt-3 text-sm text-graphite font-light leading-relaxed">{p.excerpt}</p>
                <div className="link-arrow mt-5">Read story <ArrowRight className="w-4 h-4" /></div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}

export function BlogDetail() {
  const { slug } = useParams();
  const enquiry = useEnquiry();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setP(null); setNotFound(false);
    window.scrollTo({ top: 0, behavior: "instant" });
    api.get(`/public/blog/${slug}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="container-x pt-40 pb-32"><h1 className="display-2">Story not found</h1><Link to="/blog" className="link-arrow mt-6 inline-flex">Back to journal <ArrowRight className="w-4 h-4" /></Link></div>;
  if (!p) return <div className="pt-32 pb-32 container-x"><div className="animate-pulse h-96 bg-bone" /></div>;

  const seo = p.seo || {};
  const blogSchemas = [
    breadcrumbs([{ name: "Home", path: "/" }, { name: "Stories", path: "/blog" }, { name: p.title, path: `/blog/${p.slug}` }]),
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: p.title,
      description: seo.meta_description || p.excerpt || "",
      image: mediaImage(p.cover_image) || undefined,
      datePublished: p.published_at || p.created_at,
      author: { "@type": "Organization", name: "Happy Retreats" },
    },
  ];
  return (
    <>
      <SEOHead title={seo.meta_title || p.title} description={seo.meta_description || p.excerpt} keywords={seo.keywords} ogImage={mediaImage(p.cover_image)} schema={blogSchemas} />
      <DraftBanner show={p.is_published === false} />

      <article className="pt-32 pb-32">
        <div className="container-x max-w-3xl">
          <Link to="/blog" className="link-arrow text-graphite" data-testid="blog-back"><ArrowLeft className="w-4 h-4" /> Journal</Link>
          <div className="overline mt-12">{p.category}</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] mt-6">{p.title}</h1>
          <div className="text-sm text-graphite mt-6">{p.author} · {new Date(p.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</div>
        </div>
        <div className="container-x max-w-5xl mt-12">
          <div className="aspect-[16/9] overflow-hidden relative"><SmartMedia media={p.cover_image} alt={p.title} className="w-full h-full object-cover" /></div>
        </div>
        <div className="container-x max-w-3xl mt-16">
          <div className="body-lead whitespace-pre-line text-ink/85">{p.content}</div>
          <div className="mt-20 bg-bone p-10 md:p-14">
            <div className="overline">Want to plan one?</div>
            <h3 className="font-display text-3xl mt-4">Begin a private enquiry.</h3>
            <button onClick={() => enquiry.open("")} className="btn-primary mt-8" data-testid="blog-enquire">Plan a visit</button>
          </div>
        </div>
      </article>
    </>
  );
}
